import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { v4 as uuidv4 } from 'uuid';

import { TRequestResponseInterceptor } from 'types/axios';
import { BASE_URL, PUBLIC_PATHS } from 'constants/urls';
import { applicationService } from 'services/application';
import { isProduction, configManager } from 'utils/configuration';
import { customErrorHandler } from 'hooks/customErrorHandler';

import { matchPath } from 'react-router-dom';
import { sendError } from 'utils/send';

const getConfig = configManager.getInstance().get;
const DS_CHANNEL_ID = '6';

function isNotPublicUrl(route: string): boolean {
  return !matchPath(route, {
    path: PUBLIC_PATHS,
    exact: true,
    strict: false,
  });
}

export const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'x-agentname': 'HYBRID-MOBILE-APP',
    'x-channelid': DS_CHANNEL_ID,
    'x-pid': applicationService.getPid(),
    'x-sid': applicationService.getSid(),
    'x-uniqueid': applicationService.getUniqueId(),
  },
  validateStatus: (status: number): boolean => status < 500,
});

api.interceptors.request.use(handleRequestInterceptor);
api.interceptors.response.use(handleResponseInterceptor);

function handleRequestInterceptor(config: AxiosRequestConfig) {
  if (isProduction()) config.baseURL = `${getConfig('MDC_GW_URL')}/api`;

  if (config.headers) {
    config.headers['x-correlationid'] = uuidv4();

    if (isNotPublicUrl(window.location.pathname)) {
      // eslint-disable-next-line no-param-reassign
      config.headers['x-applicationid'] =
        applicationService.getApplicationId() || '';
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization =
        `Bearer ${applicationService.getToken()}` || '';
      // eslint-disable-next-line no-param-reassign
      config.headers['x-storefrontid'] =
        applicationService.getStoreFrontId() || '';
    }
  }

  return config;
}

async function handleResponseInterceptor(
  response: AxiosResponse
): Promise<TRequestResponseInterceptor> {
  const newResponse = await customErrorHandler(response)?.();

  if (response?.status === 401) {
    const storefrontId = applicationService.getStoreFrontId();
    const applicationId = applicationService.getApplicationId();
    const token = applicationService.getToken();

    sendError(
      '401 Unauthorized',
      `storefrontId: ${storefrontId}, applicationId: ${applicationId}, token: ${token}`
    );
  }

  if (newResponse) {
    return {
      status: newResponse.status,
      data: newResponse.data,
    };
  }

  return {
    status: response.status,
    data: response.data,
  };
}

export default api;
